/**
 * API service for Reports module
 */
import api from './api';

export interface ReportTemplate {
  id: number;
  name: string;
  description: string;
  report_type: 'table' | 'chart' | 'kpi' | 'mixed';
  created_by: number;
  created_by_name: string;
  is_public: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  filters?: ReportFilter[];
  metrics?: ReportMetric[];
}

export interface ReportFilter {
  id: number;
  template: number;
  field_type: 'lead' | 'opportunity' | 'pipeline' | 'stage' | 'product' | 'user' | 'activity' | 'conversation' | 'date' | 'value';
  field_name: string;
  operator: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'icontains' | 'startswith' | 'endswith' | 'in' | 'not_in' | 'is_null' | 'is_not_null' | 'date_range' | 'last_days' | 'last_months';
  value: string;
  order: number;
}

export interface ReportMetric {
  id: number;
  template: number;
  metric_type: 'total_sales' | 'total_opportunities' | 'conversion_rate' | 'average_ticket' | 'won_deals' | 'lost_deals' | 'active_deals' | 'total_leads' | 'pipeline_value' | 'stage_count' | 'user_performance' | 'product_sales' | 'custom';
  custom_metric_name?: string;
  aggregation: 'count' | 'sum' | 'avg' | 'min' | 'max' | 'count_distinct' | 'percentage';
  field_name?: string;
  order: number;
  is_visible: boolean;
}

export interface ScheduledReport {
  id: number;
  template: number;
  template_name: string;
  name: string;
  schedule: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  run_time: string;
  recipients: string[];
  active: boolean;
  status: 'active' | 'paused' | 'completed' | 'failed';
  last_run?: string;
  next_run?: string;
  created_by: number;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface ReportGenerationRequest {
  template_id: number;
  filters?: Record<string, any>;
  start_date?: string;
  end_date?: string;
  export_format?: 'json' | 'csv' | 'excel' | 'pdf';
}

export interface ReportData {
  template: {
    id: number;
    name: string;
    description: string;
    report_type: string;
  };
  period: {
    start_date?: string;
    end_date?: string;
  };
  metrics: Array<{
    name: string;
    type: 'currency' | 'number' | 'percentage' | 'list';
    value: any;
  }>;
  total_records: number;
  generated_at: string;
}

/**
 * Report Templates API
 */
export const reportsApi = {
  // List templates
  listTemplates: async (params?: {
    report_type?: string;
    is_public?: boolean;
    is_active?: boolean;
    search?: string;
    ordering?: string;
  }) => {
    const response = await api.get('/reports/templates/', { params });
    return response.data;
  },

  // Get template detail
  getTemplate: async (id: number) => {
    const response = await api.get(`/reports/templates/${id}/`);
    return response.data;
  },

  // Create template
  createTemplate: async (data: Partial<ReportTemplate>) => {
    const response = await api.post('/reports/templates/', data);
    return response.data;
  },

  // Update template
  updateTemplate: async (id: number, data: Partial<ReportTemplate>) => {
    const response = await api.put(`/reports/templates/${id}/`, data);
    return response.data;
  },

  // Partial update template
  partialUpdateTemplate: async (id: number, data: Partial<ReportTemplate>) => {
    const response = await api.patch(`/reports/templates/${id}/`, data);
    return response.data;
  },

  // Delete template
  deleteTemplate: async (id: number) => {
    await api.delete(`/reports/templates/${id}/`);
  },

  // Duplicate template
  duplicateTemplate: async (id: number) => {
    const response = await api.post(`/reports/templates/${id}/duplicate/`);
    return response.data;
  },
};

/**
 * Scheduled Reports API
 */
export const scheduledReportsApi = {
  // List scheduled reports
  listScheduledReports: async (params?: {
    template?: number;
    schedule?: string;
    status?: string;
    active?: boolean;
    search?: string;
    ordering?: string;
  }) => {
    const response = await api.get('/reports/scheduled/', { params });
    return response.data;
  },

  // Get scheduled report detail
  getScheduledReport: async (id: number) => {
    const response = await api.get(`/reports/scheduled/${id}/`);
    return response.data;
  },

  // Create scheduled report
  createScheduledReport: async (data: Partial<ScheduledReport>) => {
    const response = await api.post('/reports/scheduled/', data);
    return response.data;
  },

  // Update scheduled report
  updateScheduledReport: async (id: number, data: Partial<ScheduledReport>) => {
    const response = await api.put(`/reports/scheduled/${id}/`, data);
    return response.data;
  },

  // Partial update scheduled report
  partialUpdateScheduledReport: async (id: number, data: Partial<ScheduledReport>) => {
    const response = await api.patch(`/reports/scheduled/${id}/`, data);
    return response.data;
  },

  // Delete scheduled report
  deleteScheduledReport: async (id: number) => {
    await api.delete(`/reports/scheduled/${id}/`);
  },

  // Activate scheduled report
  activateScheduledReport: async (id: number) => {
    const response = await api.post(`/reports/scheduled/${id}/activate/`);
    return response.data;
  },

  // Deactivate scheduled report
  deactivateScheduledReport: async (id: number) => {
    const response = await api.post(`/reports/scheduled/${id}/deactivate/`);
    return response.data;
  },

  // Run report now
  runScheduledReportNow: async (id: number) => {
    const response = await api.post(`/reports/scheduled/${id}/run_now/`);
    return response.data;
  },
};

/**
 * Report Generation API
 */
export const reportGenerationApi = {
  // Generate report
  generateReport: async (request: ReportGenerationRequest) => {
    const response = await api.post('/reports/generate/', request);
    
    // For file exports (csv, excel, pdf), the response is a blob
    if (request.export_format && request.export_format !== 'json') {
      return response;
    }
    
    return response.data;
  },
};
